/**
 * A conferência das etapas na tela (E147.F3, ADR-0101): lê ao montar e a cada `releitura` (o caso
 * relido depois do exame) e aplica o pedido da equipe. O pedido de tentativa verifica o caso na mesma
 * chamada, e o servidor responde só o pedido gravado: a tela relê as etapas depois, atendido ou
 * recusado por já não haver falha (a tela estava velha).
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  EtapasDoCasoError,
  PedidoDaEtapaError,
  lerEtapasDoCaso,
  pedirNaEtapa,
  type EtapasDoCasoData,
  type MotivoDaFalhaDasEtapas,
  type PedidoDaEtapaData,
  type RecusaDoPedidoDaEtapa,
} from '@/lib/conferencia_das_etapas';

export type EstadoDasEtapasNaTela =
  | { estado: 'carregando' }
  | { estado: 'erro'; motivo: MotivoDaFalhaDasEtapas }
  | { estado: 'pronto'; dados: EtapasDoCasoData };

export type ResultadoDoPedidoDaEtapa = { ok: true } | { ok: false; recusa: RecusaDoPedidoDaEtapa };

export interface UsoDasEtapas {
  etapas: EstadoDasEtapasNaTela;
  reler: () => void;
  pedir: (pedido: PedidoDaEtapaData) => Promise<ResultadoDoPedidoDaEtapa>;
}

export function useConferenciaDasEtapas(
  client: AxiosInstance,
  caseId: string,
  releitura: unknown,
): UsoDasEtapas {
  const [etapas, setEtapas] = useState<EstadoDasEtapasNaTela>({ estado: 'carregando' });
  const [pedido, setPedido] = useState(0);
  const reler = useCallback(() => setPedido((n) => n + 1), []);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const dados = await lerEtapasDoCaso(client, caseId);
        if (vivo) setEtapas({ estado: 'pronto', dados });
      } catch (err) {
        if (!vivo) return;
        const motivo = err instanceof EtapasDoCasoError ? err.motivo : 'indisponivel';
        setEtapas({ estado: 'erro', motivo });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [client, caseId, releitura, pedido]);

  const pedir = useCallback(
    async (corpo: PedidoDaEtapaData): Promise<ResultadoDoPedidoDaEtapa> => {
      try {
        await pedirNaEtapa(client, caseId, corpo);
        reler();
        return { ok: true };
      } catch (err) {
        const recusa = err instanceof PedidoDaEtapaError ? err.recusa : 'indisponivel';
        if (recusa === 'sem_falha') reler();
        return { ok: false, recusa };
      }
    },
    [client, caseId, reler],
  );

  return { etapas, reler, pedir };
}
